import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { statusLabel } from "../../lib/statusLabel";
import { useLocalization } from "../../hooks/useLocalization";

const ESTABLISHMENT_STATUSES = ["NOT_ESTABLISHED", "REVIEW_REQUIRED", "ESTABLISHED"];

export default function HealthCommitteeDashboardPage() {
  const { t } = useLocalization();
  const { canViewHealthCommittee, canManageHealthCommittee } = usePermissions();
  const [config, setConfig] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    healthCommitteeApi.getConfig().then((r) => { setConfig(r.data); setNote(r.data.establishment_note || ""); }).catch(() => {});
    healthCommitteeApi.listMeetings().then((r) => setMeetings(r.data)).catch(() => {});
    healthCommitteeApi.listActionItems().then((r) => setActionItems(r.data)).catch(() => {});
  };
  useEffect(() => { if (canViewHealthCommittee) load(); }, [canViewHealthCommittee]);

  if (!canViewHealthCommittee) return <p className="text-sm text-gray-500" data-testid="health-committee-access-denied">{t("common.accessDenied")}</p>;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await healthCommitteeApi.updateConfig(status, note);
      toast.success(t("mgmtHealthCommittee.establishmentStatusUpdated"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openActionItems = actionItems.filter((a) => !["COMPLETED", "CANCELLED"].includes(a.status));
  const finalizedCount = meetings.filter((m) => m.status === "FINALIZED").length;

  return (
    <div className="space-y-5" data-testid="health-committee-dashboard-page">
      <PageHelpBanner pageKey="health-committee-dashboard" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtHealthCommittee.dashboardHelp")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtHealthCommittee.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtHealthCommittee.subtitle")}</p>
      </div>

      {config && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="health-committee-config-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-gray-700">{t("mgmtHealthCommittee.establishmentStatus")}</h3>
            <StatusBadge status={config.establishment_status} />
          </div>
          {canManageHealthCommittee && (
            <div className="flex flex-wrap gap-2 items-end">
              <textarea data-testid="health-committee-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                className="flex-1 min-w-[240px] border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtHealthCommittee.notePlaceholder")} />
              {ESTABLISHMENT_STATUSES.map((s) => (
                <button key={s} data-testid={`health-committee-set-status-${s}`} disabled={busy || s === config.establishment_status} onClick={() => setStatus(s)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border disabled:opacity-40 ${s === config.establishment_status ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 hover:bg-gray-50"}`}>
                  {statusLabel(t, s)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/management/compliance/health-committee/meetings" data-testid="health-committee-meetings-card" className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <p className="text-2xl font-bold text-gray-900">{meetings.length}</p>
          <p className="text-xs text-gray-500">{t("mgmtHealthCommittee.meetingsRecorded")} ({finalizedCount} {t("mgmtHealthCommittee.finalized")})</p>
        </Link>
        <Link to="/management/compliance/health-committee/actions" data-testid="health-committee-actions-card" className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <p className="text-2xl font-bold text-gray-900">{openActionItems.length}</p>
          <p className="text-xs text-gray-500">{t("mgmtHealthCommittee.openActionItems")}</p>
        </Link>
        <Link to="/management/compliance/health-committee/members" data-testid="health-committee-members-card" className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <p className="text-2xl font-bold text-gray-900">→</p>
          <p className="text-xs text-gray-500">{t("mgmtHealthCommittee.manageMembers")}</p>
        </Link>
      </div>

      {canManageHealthCommittee && (
        <Link to="/management/compliance/health-committee/meetings/new" data-testid="health-committee-new-meeting-link" className="inline-block px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">
          + {t("mgmtHealthCommittee.newMeeting")}
        </Link>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">{t("mgmtHealthCommittee.recentMeetings")}</h3></div>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-2 font-medium">{t("common.date")}</th><th className="px-4 py-2 font-medium">{t("mgmtHealthCommittee.colType")}</th><th className="px-4 py-2 font-medium">{t("common.status")}</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {meetings.slice(0, 8).map((m) => (
              <tr key={m.id} className="border-t border-gray-100" data-testid={`health-committee-meeting-row-${m.id}`}>
                <td className="px-4 py-2 text-gray-700">{m.meeting_date}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.meeting_type}</td>
                <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-2"><Link to={`/management/compliance/health-committee/meetings/${m.id}`} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`health-committee-meeting-view-${m.id}`}>{t("mgmtLongHourInterview.view")}</Link></td>
              </tr>
            ))}
            {meetings.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-health-committee-meetings">{t("mgmtHealthCommittee.noMeetingsRecorded")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
